import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import CheckIn from "@/pages/CheckIn";
import CrewCheckIn from "@/pages/CrewCheckIn";
import ResetPassword from "@/pages/ResetPassword";
import SetupAccount from "@/pages/SetupAccount";
import ChangePin from "@/pages/ChangePin";
import SetupStudio from "@/pages/SetupStudio";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import People from "@/pages/People";
import Classes from "@/pages/Classes";
import Videos from "@/pages/Videos";
import Curriculum from "@/pages/Curriculum";
import Assess from "@/pages/Assess";
import Progress from "@/pages/Progress";
import Advancement from "@/pages/Advancement";
import Charms from "@/pages/Charms";
import Directors from "@/pages/Directors";
import Announcements from "@/pages/Announcements";
import NHSDA from "@/pages/NHSDA";
import Documents from "@/pages/Documents";
import StudioSettings from "@/pages/StudioSettings";
import Calendar from "@/pages/Calendar";
import ProductionHub from "@/pages/ProductionHub";
import Consent from "@/pages/Consent";
import ChildrenPrivacy from "@/pages/ChildrenPrivacy";
import ConsentGate from "@/components/ConsentGate";
import Coverage from "@/pages/Coverage";
import LessonPlans from "@/pages/LessonPlans";
import Messages from "@/pages/Messages";
import Attendance from "@/pages/Attendance";
import Timesheets from "@/pages/Timesheets";
import JoinRequests from "@/pages/JoinRequests";
import Pickup from "@/pages/Pickup";
import Rentals from "@/pages/Rentals";
import CostumeHub from "@/pages/CostumeHub";
import RecitalHub from "@/pages/RecitalHub";
import Leads from "@/pages/Leads";
import { saveReturn } from "@/lib/returnTo";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) { saveReturn(window.location.pathname + window.location.search); return <Navigate to="/login" replace />; }
  if (user.role === "dancer" && user.must_change_pin) return <ChangePin />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (user.role === "dancer") return <ConsentGate><Layout>{children}</Layout></ConsentGate>;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/crew/:token" element={<CrewCheckIn />} />
      <Route path="/checkin/:pid/:date" element={<Protected roles={["director", "teacher"]}><CheckIn /></Protected>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup-account" element={<SetupAccount />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/people" element={<Protected roles={["director"]}><People /></Protected>} />
      <Route path="/join-requests" element={<Protected roles={["director"]}><JoinRequests /></Protected>} />
      <Route path="/classes" element={<Protected><Classes /></Protected>} />
      <Route path="/videos" element={<Protected><Videos /></Protected>} />
      <Route path="/consent" element={<Protected roles={["parent"]}><Consent /></Protected>} />
      <Route path="/children-privacy" element={<Protected roles={["director"]}><ChildrenPrivacy /></Protected>} />
      <Route path="/curriculum" element={<Protected roles={["director", "teacher"]}><Curriculum /></Protected>} />
      <Route path="/assess" element={<Protected roles={["director", "teacher"]}><Assess /></Protected>} />
      <Route path="/advancement" element={<Protected roles={["director", "teacher"]}><Advancement /></Protected>} />
      <Route path="/charms" element={<Protected><Charms /></Protected>} />
      <Route path="/announcements" element={<Protected><Announcements /></Protected>} />
      <Route path="/nhsda" element={<Protected><NHSDA /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/production" element={<Protected roles={["director", "teacher", "dancer", "parent"]}><ProductionHub /></Protected>} />
      <Route path="/costumes" element={<Protected roles={["director", "teacher", "parent", "dancer"]}><CostumeHub /></Protected>} />
      <Route path="/recital" element={<Protected roles={["parent", "dancer"]}><RecitalHub /></Protected>} />
      <Route path="/costume-inventory" element={<Navigate to="/costumes" replace />} />
      <Route path="/coverage" element={<Protected roles={["director", "teacher"]}><Coverage /></Protected>} />
      <Route path="/lesson-plans" element={<Protected roles={["director", "teacher"]}><LessonPlans /></Protected>} />
      <Route path="/messages" element={<Protected roles={["director", "teacher"]}><Messages /></Protected>} />
      <Route path="/attendance" element={<Protected roles={["director", "teacher"]}><Attendance /></Protected>} />
      <Route path="/pickup" element={<Protected roles={["director", "teacher", "parent"]}><Pickup /></Protected>} />
      <Route path="/rentals" element={<Navigate to="/costumes" replace />} />
      <Route path="/leads" element={<Protected roles={["director", "teacher"]}><Leads /></Protected>} />
      <Route path="/timesheets" element={<Protected roles={["director", "teacher"]}><Timesheets /></Protected>} />
      <Route path="/settings" element={<Protected roles={["director"]}><StudioSettings /></Protected>} />
      <Route path="/directors" element={<Protected roles={["director"]}><Directors /></Protected>} />
      <Route path="/progress" element={<Protected roles={["dancer", "parent"]}><Progress /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function SetupGate({ children }) {
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setState("ready"); return; }
    api.get("/auth/setup-status")
      .then((r) => setState(r.data.needs_setup ? "setup" : "ready"))
      .catch(() => setState("ready"));
  }, []);
  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-teal border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (state === "setup") return <SetupStudio />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SetupGate>
            <AppRouter />
          </SetupGate>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
